import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "suggestions", "lat", "lng", "submitButton", "loadingIndicator"]
  static values = {
    mapboxToken: String,
    contractorSubdomain: String
  }

  connect() {
    this.debounceTimer = null
    this.selectedIndex = -1
    this.hasValidAddress = false
    this.loadMapboxToken()
    this.checkExistingAddress()
    this.bindKeyboardEvents()
    this.createPortalDropdown()
  }

  disconnect() {
    this.unbindKeyboardEvents()
    this.removePortalDropdown()
  }

  createPortalDropdown() {
    // Create a dropdown element that lives at body level to escape overflow containers
    this.portalDropdown = document.createElement('div')
    this.portalDropdown.className = 'fixed z-[9999] bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg hidden max-h-60 overflow-y-auto'
    this.portalDropdown.style.display = 'none'
    document.body.appendChild(this.portalDropdown)

    // Use event delegation for click handling (more efficient)
    this.portalDropdown.addEventListener('click', (e) => {
      const item = e.target.closest('.suggestion-item')
      if (item) {
        this.selectAddressFromItem(item)
      }
    })
  }

  removePortalDropdown() {
    if (this.portalDropdown && this.portalDropdown.parentNode) {
      this.portalDropdown.parentNode.removeChild(this.portalDropdown)
    }
  }

  bindKeyboardEvents() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.inputTarget.addEventListener('keydown', this.handleKeydown)
  }

  unbindKeyboardEvents() {
    this.inputTarget.removeEventListener('keydown', this.handleKeydown)
  }

  handleKeydown(event) {
    const items = this.portalDropdown?.querySelectorAll('.suggestion-item') || []

    // If no suggestions showing, let the form submit normally (especially for pre-filled demo)
    if (items.length === 0) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.selectedIndex = Math.min(this.selectedIndex + 1, items.length - 1)
        this.highlightItem(items)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.selectedIndex = Math.max(this.selectedIndex - 1, 0)
        this.highlightItem(items)
        break
      case 'Enter':
        // If a suggestion is highlighted, select it
        if (this.selectedIndex >= 0 && items[this.selectedIndex]) {
          event.preventDefault()
          this.selectAddressFromItem(items[this.selectedIndex])
        } else if (this.hasValidAddress) {
          // If we have a valid pre-filled address, allow form submission
          // Clear suggestions and let the form submit
          this.hideSuggestions()
        } else {
          // No valid address and no selection - prevent submission, select first suggestion
          event.preventDefault()
          if (items.length > 0) {
            this.selectedIndex = 0
            this.highlightItem(items)
          }
        }
        break
      case 'Escape':
        this.hideSuggestions()
        break
    }
  }

  highlightItem(items) {
    items.forEach((item, index) => {
      if (index === this.selectedIndex) {
        item.classList.add('is-selected')
        item.scrollIntoView({ block: 'nearest' })
      } else {
        item.classList.remove('is-selected')
      }
    })
  }

  checkExistingAddress() {
    // Enable submit button if address is pre-populated with lat/lng
    if (this.hasLatTarget && this.hasLngTarget) {
      const hasLat = this.latTarget.value && this.latTarget.value.trim() !== ''
      const hasLng = this.lngTarget.value && this.lngTarget.value.trim() !== ''

      if (hasLat && hasLng) {
        this.hasValidAddress = true
        this.enableSubmitButton()
      }
    }
  }

  enableSubmitButton() {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
    }

    // Also sync the visible button in bottom bar
    const visibleBtn = document.getElementById('visible-address-submit-btn')
    if (visibleBtn) {
      visibleBtn.disabled = false
    }
  }

  showLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('hidden')
    }
  }

  async loadMapboxToken() {
    try {
      const response = await fetch('/api/widget/config')
      const data = await response.json()
      this.mapboxTokenValue = data.mapbox_token
    } catch (error) {
      console.error('Failed to load Mapbox token:', error)
    }
  }

  search(event) {
    const query = event.target.value
    clearTimeout(this.debounceTimer)

    // When user types, invalidate the current address until they select a new one
    this.hasValidAddress = false

    if (query.length < 3) {
      this.hideSuggestions()
      return
    }

    this.debounceTimer = setTimeout(() => {
      this.fetchSuggestions(query)
    }, 150)
  }

  async fetchSuggestions(query) {
    if (!this.mapboxTokenValue) return

    this.showLoading()

    try {
      const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${this.mapboxTokenValue}&types=address&country=us&limit=3`
      const response = await fetch(url)
      const data = await response.json()

      this.hideLoading()

      if (data.features && data.features.length > 0) {
        this.renderSuggestions(data.features)
      } else {
        this.hideSuggestions()
      }
    } catch (error) {
      this.hideLoading()
      console.error('Geocoding error:', error)
    }
  }

  renderSuggestions(features) {
    // Reset keyboard selection when new suggestions arrive
    this.selectedIndex = -1

    const html = features.map(feature => {
      const mainText = feature.place_name.split(',')[0]
      const subText = feature.place_name.split(',').slice(1).join(',').trim()

      return `
        <div class="suggestion-item p-3 cursor-pointer border-b border-gray-100 last:border-b-0 hover:bg-gray-50"
             data-address="${this.escapeHtml(feature.place_name)}"
             data-lat="${feature.center[1]}"
             data-lng="${feature.center[0]}">
          <div class="font-medium text-gray-900">${mainText}</div>
          <div class="text-sm text-gray-500">${subText}</div>
        </div>
      `
    }).join('')

    this.portalDropdown.innerHTML = html
    this.positionDropdown()
    this.portalDropdown.style.display = 'block'
    this.portalDropdown.classList.remove('hidden')
  }

  positionDropdown() {
    const inputRect = this.inputTarget.getBoundingClientRect()

    this.portalDropdown.style.top = `${inputRect.bottom + 4}px`
    this.portalDropdown.style.left = `${inputRect.left}px`
    this.portalDropdown.style.width = `${inputRect.width}px`
  }

  selectAddress(event) {
    this.selectAddressFromItem(event.currentTarget)
  }

  selectAddressFromItem(item) {
    const address = item.dataset.address
    const lat = item.dataset.lat
    const lng = item.dataset.lng

    this.inputTarget.value = address
    this.latTarget.value = lat
    this.lngTarget.value = lng
    this.hideSuggestions()
    this.hasValidAddress = true

    this.enableSubmitButton()

    // Preload Mapbox resources for the pool verification step
    this.preloadMapbox()
  }

  preloadMapbox() {
    // Skip if already preloaded
    if (document.querySelector('link[href*="mapbox-gl"]')) return

    // Preload Mapbox GL JS
    const scriptPreload = document.createElement('link')
    scriptPreload.rel = 'preload'
    scriptPreload.as = 'script'
    scriptPreload.href = 'https://api.mapbox.com/mapbox-gl-js/v3.2.0/mapbox-gl.js'
    document.head.appendChild(scriptPreload)

    // Preload Mapbox GL CSS
    const cssPreload = document.createElement('link')
    cssPreload.rel = 'preload'
    cssPreload.as = 'style'
    cssPreload.href = 'https://api.mapbox.com/mapbox-gl-js/v3.2.0/mapbox-gl.css'
    document.head.appendChild(cssPreload)

    // Also load the CSS immediately for faster visual rendering
    const cssLink = document.createElement('link')
    cssLink.rel = 'stylesheet'
    cssLink.href = 'https://api.mapbox.com/mapbox-gl-js/v3.2.0/mapbox-gl.css'
    document.head.appendChild(cssLink)
  }

  hideSuggestions() {
    if (this.portalDropdown) {
      this.portalDropdown.innerHTML = ''
      this.portalDropdown.style.display = 'none'
      this.portalDropdown.classList.add('hidden')
    }
    this.selectedIndex = -1
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }
}
